import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

// Components
import TagsSearchBar from './TagsSearchBar';
import TagsCreateButtons from './TagsCreateButtons';
import TagsList from './TagsList';

// Utils
import { tagPathDisplaySelector } from './tagPathDisplaySelector';
import { TagsToolbarPopupProps } from '../../workspace/toolbar/components/selectionTools/tagsTool/TagsToolbarPopup';
import { getElementId, getTags } from '../../../common/elements/utils/elementPropertyUtils';
import { addTagToElements, removeTagFromElements } from './tagsActions';
import { fetchTagsFromDb } from './tagsService';

// Types
import { Tag } from '../../../common/tags/tagTypes';

import './TagsPopupContent.scss';

type TagsPopupContentProps = TagsToolbarPopupProps & {
    handleClickEdit: (tagId: string) => void;
};

const TagsPopupContent = (props: TagsPopupContentProps): ReactElement => {
    const { currentBoardId, selectedElements, handleClickEdit } = props;

    const [searchString, setSearchString] = React.useState<string>('');
    const [tags, setTags] = useState<Array<Tag>>([]);
    const rootPaths = useSelector(tagPathDisplaySelector);
    const { userRootPath, shareRootPath } = rootPaths || {};

    const dispatch = useDispatch();
    const handleAddTagToElement = (tagId: string) => dispatch(addTagToElements(selectedElements, tagId));
    const handleRemoveTagFromElement = (tagId: string) => dispatch(removeTagFromElements(selectedElements, tagId));
    const handleClickSelectedTag = (tagId: string) => {
        const isTagSelectedForEveryElement = selectedElements.every((element) =>
            (getTags(element) || []).includes(tagId),
        );
        const updateFunction = isTagSelectedForEveryElement ? handleRemoveTagFromElement : handleAddTagToElement;

        updateFunction(tagId);
    };

    useEffect(() => {
        const ancestors = userRootPath?.map((element) => getElementId(element));
        fetchTagsFromDb({ boardIds: ancestors || [currentBoardId] }).then(setTags);
    }, []);

    const filteredTags = useMemo(() => {
        if (isEmpty(searchString)) return tags;

        const value = searchString!.toLowerCase();
        return tags.filter((tag) => tag.name.toLowerCase().includes(value));
    }, [tags, searchString]);

    const elementTagIds = selectedElements.flatMap((element) => getTags(element) || []);
    const [selectedTags, unselectedTags] = filteredTags.reduce(
        (accumulator: [Tag[], Tag[]], tag) => {
            elementTagIds?.includes(tag._id) ? accumulator[0].push(tag) : accumulator[1].push(tag);
            return accumulator;
        },
        [[], []],
    );

    return (
        <div className="TagsPopupContent">
            <TagsSearchBar value={searchString} onChange={setSearchString} />
            <TagsList
                tagsArray={selectedTags}
                handleClickTag={handleClickSelectedTag}
                handleClickEdit={handleClickEdit}
                isSelected
            />
            <TagsList
                tagsArray={unselectedTags}
                handleClickTag={handleAddTagToElement}
                handleClickEdit={handleClickEdit}
            />
            {searchString && (
                <TagsCreateButtons
                    {...props}
                    shareRootPath={shareRootPath}
                    searchString={searchString}
                    setSearchString={setSearchString}
                />
            )}
        </div>
    );
};

export default TagsPopupContent;
